import React, { useRef, useState } from "react";
import { gecersizMailler, mailListesiDuzenle } from "../helpers/mailListesi";

interface Props {
    labelText: string
    value: string | undefined
    setValue: (_value: string | undefined) => void
    rows?: number
}

/**
 * Virgülle ayrılmış e-posta listesi alanı (çok satırlı). Yazmanın ortasında metin değiştirilmez,
 * imleç yerinden oynamaz; liste yalnız şu anlarda "a@x.com, b@y.net" biçimine getirilir:
 * - yapıştırınca (bitişik yapıştırılan adresler de ayrılır),
 * - metnin sonunda boşluk, virgül, noktalı virgül ya da Enter'a basınca,
 * - alandan çıkınca.
 * Geçersiz adres varsa alan odakta değilken altında kırmızı uyarı görünür.
 */
const MailListesiAlani = ({ labelText, value, setValue, rows = 3 }: Props) => {
    const ref = useRef<HTMLTextAreaElement>(null);
    const [odakta, setOdakta] = useState<boolean>(false);

    const gecersizler = odakta ? [] : gecersizMailler(value);

    const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const yapistirilan = e.clipboardData.getData('text');

        if (!yapistirilan)
            return;

        e.preventDefault();

        const alan = e.currentTarget;
        const mevcut = alan.value;
        const once = mevcut.substring(0, alan.selectionStart) + yapistirilan;
        const sonra = mevcut.substring(alan.selectionEnd);

        // Araya ayraç konmaz: adresin ortasına yapıştırma bozulmasın; bitişik kalan adresler zaten ayrılır.
        const yeni = mailListesiDuzenle(once + sonra);
        // İmleç yapıştırılan son adresin hemen arkasına (ardından gelen ", " geçilerek) konur.
        const onceDuzenli = mailListesiDuzenle(once);
        const imlec = sonra.trim() ? Math.min(onceDuzenli.length + 2, yeni.length) : yeni.length;

        setValue(yeni);

        setTimeout(() => {
            if (ref.current)
                ref.current.setSelectionRange(imlec, imlec);
        }, 0);
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (![' ', ',', ';', 'Enter'].includes(e.key))
            return;

        const alan = e.currentTarget;

        // Yalnız metnin sonunda yazarken düzenlenir; ortada yazarken tuş olduğu gibi çalışır.
        if (alan.selectionStart !== alan.value.length || alan.selectionEnd !== alan.value.length)
            return;

        e.preventDefault();

        const duzenli = mailListesiDuzenle(alan.value);
        setValue(duzenli ? duzenli + ', ' : '');
    }

    const onBlur = () => {
        setOdakta(false);

        const duzenli = mailListesiDuzenle(value);

        if (duzenli !== (value ?? ''))
            setValue(duzenli || undefined);
    }

    return (
        <React.Fragment>
            <label className="form-label">{labelText}</label>
            <textarea
                ref={ref}
                rows={rows}
                value={value ?? ''}
                onChange={(e) => setValue(e.target.value)}
                onPaste={onPaste}
                onKeyDown={onKeyDown}
                onFocus={() => setOdakta(true)}
                onBlur={onBlur}
                placeholder="ornek@firma.com, ornek2@firma.com.tr"
                style={{ border: '1px solid #F1F1F4', width: '100%', resize: 'vertical' }}
                className="form-control form-control-solid"
            />
            {
                gecersizler.length > 0 && (
                    <div className='text-danger' style={{ fontSize: 11.5, marginTop: 4 }}>
                        Geçersiz e-posta adresi: {gecersizler.join(', ')}
                    </div>
                )
            }
        </React.Fragment>
    )
}

export default MailListesiAlani;
